import { Injectable } from "@angular/core";
import { GenericOAuth2 } from "@capacitor-community/generic-oauth2";
import { Platform } from "@ionic/angular";

import { Observable, forkJoin, from, of } from "rxjs";
import { catchError, filter, map, switchMap, tap } from "rxjs/operators";

import { environment } from "../../../environments/environment";

import { StorageService } from "./storage.service";
import { UserService } from "./user.service";
import { WindowRefService } from "./window-ref.service";
import { Router } from "@angular/router";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  oauth2Options = {
    appId: environment.b2cConfig.clientID,
    authorizationBaseUrl: `${environment.b2cConfig.authority}/oauth2/v2.0/authorize`,
    accessTokenEndpoint: "",
    scope: environment.b2cConfig.b2cScopes[0],
    responseType: "token",
    web: {
      redirectUrl: environment.b2cConfig.redirectUri,
      windowTarget: "_self",
    },
    android: {
      pkceEnabled: true,
      responseType: "code",
      redirectUrl: environment.b2cConfig.redirectUriMobile,
      accessTokenEndpoint: environment.b2cConfig.accessTokenEndpoint,
      handleResultOnNewIntent: true,
      handleResultOnActivityResult: true,
    },
    ios: {
      pkceEnabled: true,
      redirectUrl: "msauth.com.fleetcare.fleetdynamics://auth",
      accessTokenEndpoint: environment.b2cConfig.accessTokenEndpoint,
    },
  };

  isSetAccessToken$: Observable<boolean> = this.userService.accessToken$.pipe(
    filter((res) => !!res),
    map(() => true)
  );

  constructor(
    private userService: UserService,
    private storageService: StorageService,
    private platform: Platform,
    private router: Router,
    private windowRefService: WindowRefService
  ) {}

  checkoutAccount(): boolean {
    const acc = localStorage.getItem("decoded");
    return !!acc;
  }

  login() {
    GenericOAuth2.authenticate(this.oauth2Options)
      .then((response) => {
        this.userService.setAccessToken(
          response.access_token || response.access_token_response.access_token
        );
      })
      .catch((reason) => {
        console.error("OAuth rejected ", reason);
        this.logout();
      });
  }

  private checkAndClearInvalidUserData(): Observable<void> {
    return from(this.storageService.get("currentUser")).pipe(
      switchMap((user) => {
        if (user === null || user === undefined) {
          // If user data is invalid, clear it and force a logout
          return from(this.storageService.remove("currentUser")).pipe(
            tap(() => {
              this.userService.logout();
              this.redirectToLogin();
            })
          );
        }
        return of(undefined);
      }),
      catchError((error) => {
        console.error("Error checking user data:", error);
        // If there's an error reading user data, force a logout
        this.userService.logout();
        this.redirectToLogin();
        return of(undefined);
      })
    );
  }

  private redirectToLogin() {
    this.router.navigate(["/login"]);
  }

  logout() {
    GenericOAuth2.logout(this.oauth2Options)
      .catch((reason) => {
        console.error("OAuth logout failed", reason);
      })
      .finally(() => this.clearSessionAndRedirect());
  }

  private clearSessionAndRedirect() {
    this.userService.logout();

    forkJoin([
      this.storageService.remove("accessToken"),
      this.storageService.remove("currentUser"),
      this.storageService.remove("isNotLinkedUser"),
      this.storageService.remove("impersonatedUserId"),
    ]).subscribe({
      next: () => this.redirectToIdpLogout(),
      error: () => this.redirectToIdpLogout(),
    });
  }

  private redirectToIdpLogout() {
    const isWeb =
      this.platform.is("mobileweb") || this.platform.is("desktop");
    const postLogoutRedirectUri = isWeb
      ? environment.b2cConfig.redirectUri
      : environment.b2cConfig.redirectUriMobile;

    this.windowRefService.window.location.href = `${
      environment.b2cConfig.logoutEndpoint
    }?p=${environment.b2cConfig.signInSignOutPolicy}&post_logout_redirect_uri=${encodeURIComponent(
      postLogoutRedirectUri
    )}`;

    if (!isWeb) {
      this.windowRefService.window.location.reload();
    }
  }
}
